import type { ReactNode } from "react";

import { EmptyField } from "@/ui/entity/renderer/empty-field";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/ui/shadcn/component/hover-card";
import { cn } from "@/ui/shadcn/lib/utils";

type NumberFormat = "plain" | "percent" | "currency" | "unit";
type NumberVariant = "positive-good" | "negative-good";

interface NumberFieldProps {
  value: number | null | undefined;
  format?: NumberFormat;
  unit?: string;
  decimals?: number;
  variant?: NumberVariant;
  hovercard?: ReactNode;
  className?: string;
}

function formatNum(
  value: number,
  format: NumberFormat,
  unit?: string,
  decimals?: number,
): string {
  const frac: Intl.NumberFormatOptions = {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  };

  switch (format) {
    case "currency":
      return new Intl.NumberFormat("en-US", {
        ...frac,
        style: "currency",
        currency: unit ?? "USD",
      }).format(value);
    case "unit":
      return new Intl.NumberFormat("en-US", {
        ...frac,
        style: "unit",
        unit,
      }).format(value);
    case "percent":
      return `${new Intl.NumberFormat("en-US", frac).format(value)}%`;
    default:
      return new Intl.NumberFormat("en-US", frac).format(value);
  }
}

function variantColor(
  value: number,
  variant?: NumberVariant,
): string | undefined {
  if (!variant || value === 0) {
    return undefined;
  }
  const isGood = variant === "positive-good" ? value > 0 : value < 0;
  return isGood
    ? "text-green-600 dark:text-green-400"
    : "text-red-600 dark:text-red-400";
}

export function NumberField({
  value,
  format = "plain",
  unit,
  decimals,
  variant,
  hovercard,
  className,
}: NumberFieldProps) {
  if (value == null) {
    return <EmptyField />;
  }

  const formatted = formatNum(value, format, unit, decimals);
  const color = variantColor(value, variant);

  const text = (
    <span
      className={cn(
        "text-sm tabular-nums",
        color,
        hovercard &&
          "underline decoration-dotted decoration-muted-foreground/40 underline-offset-2",
        className,
      )}>
      {formatted}
    </span>
  );

  if (!hovercard) {
    return text;
  }

  return (
    <HoverCard>
      {/* A span, not the default anchor: a number is not a link, and a
          number inside a link would nest anchors. */}
      <HoverCardTrigger render={<span />}>{text}</HoverCardTrigger>
      <HoverCardContent side="top" className="w-auto">
        {hovercard}
      </HoverCardContent>
    </HoverCard>
  );
}
